import React, { useEffect, useState } from 'react';
import './usercontent.css'
import android from '../../../assets/android.webp';
import { useParams } from 'react-router-dom';
import defaultimg from '../../../assets/defaultimg.webp';
import ContentUnavailable from '../../ContentUnavaliable/contentunavaliable';

const API_URL = process.env.REACT_APP_API_URL;

const UserContent = () => {
  const [user, setUser] = useState({});
  const [expdetails, setExpDetails] = useState([]);
  const [edudetails, setEduDetails] = useState([]);
  const [certdetails, setCertDetails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [contentRestricted, setContentRestricted] = useState(false);
  const [restrictionInfo, setRestrictionInfo] = useState(null);
  const { id } = useParams();

  const formatDate = (dateString) => {
    if (!dateString) return 'Present';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      year: 'numeric'
    });
  };

  // Fixed sortByDate function to handle null or undefined values
  const sortByDate = (items) => {
    if (!items || !Array.isArray(items) || items.length === 0) {
      return [];
    }
    
    return [...items].sort((a, b) => {
      // Ensure both dates exist before comparing
      if (!a.start_date) return 1;  // Push items without start_date to the end
      if (!b.start_date) return -1; // Keep items with start_date at the beginning
      
      // Sort by start_date in descending order (most recent first)
      return new Date(b.start_date) - new Date(a.start_date);
    });
  };

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const response = await fetch(`${API_URL}/user/${id}`);
        
        // Check if response is not OK
        if (!response.ok) {
          // Check if the content is unavailable due to moderation
          if (response.status === 403) {
            try {
              const errorData = await response.json();
              if (errorData.error === "Content unavailable") {
                setContentRestricted(true);
                setRestrictionInfo({
                  reason: errorData.reason,
                  expiresAt: errorData.expires_at
                });
                setLoading(false);
                return;
              }
            } catch (jsonError) {
              // If we can't parse JSON, continue with regular error handling
              console.error('Error parsing JSON from response:', jsonError);
            }
          }
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        
        // Safely set data with null checks
        setUser(data.user_details || {});
        setExpDetails(sortByDate(data.experience || []));
        setEduDetails(sortByDate(data.education || []));
        setCertDetails(sortByDate(data.certification || []));
      } catch (error) {
        console.error('Error fetching user', error);
        setError(error.message || "Failed to load user data");
      } finally {
        setLoading(false);
      }
    };
    
    fetchUser();
  }, [id]);

  const handleChatClick = (recipientId) => {
    const userId = localStorage.getItem('loginId');
    if (!userId) {
      // Handle not logged in case
      alert("Please log in to start a chat");
      return;
    }
    // Navigate to chat page with recipientId in the URL
    window.location.href = `/chat?recipient=${recipientId}`;
  };
  
  // Safely process arrays by ensuring they're valid arrays first
  const projectTitlesArray = user.project_title ? user.project_title.split(',').map(title => title.trim()) : [];
  const projectIDsArray = user.project_id ? user.project_id.split(',').map(id => id.trim()) : [];
  const topSkillsArray = user.skill_names ? user.skill_names.split(',').map(skill => skill.trim()) : [];
  const portfolioTitlesArray = user.portfolio_title ? user.portfolio_title.split(',').map(title => title.trim()) : [];
  const portfoliohyperlinksArray = user.portfolio_link ? user.portfolio_link.split(',').map(links => links.trim()) : [];
  
  const [showReportDialog, setShowReportDialog] = useState(false);
  const [reportReason, setReportReason] = useState('');
  const [reportDescription, setReportDescription] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleReport = async () => { // Function to handle report submission
    if (!reportReason || !reportDescription) {
      alert('Please fill in all fields');
      return;
    }
  
    setIsSubmitting(true);
    try { // Start the report submission process
      const response = await fetch(`${API_URL}/report`, { 
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          contentType: 'profile',
          contentId: parseInt(id),
          category: reportReason,
          description: reportDescription
        })
      });
      
      if (!response.ok) throw new Error('Failed to submit report');
  
      alert('Report submitted successfully');
      setShowReportDialog(false);
      setReportReason('');
      setReportDescription('');
    } catch (error) {
      console.error('Error submitting report:', error);
      alert('Failed to submit report. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  if (loading) {
    return <div className="userbody">Loading...</div>;
  }

  if (contentRestricted) {
    return (
      <div className="userbody">
        <ContentUnavailable 
          reason={restrictionInfo?.reason} 
          expiresAt={restrictionInfo?.expiresAt}
        />
      </div>
    );
  }

  if (error) {
    return <div className="userbody">Error: {error}</div>;
  }

  // Check if we have minimal required user data
  if (!user || !user.first_name) {
    return <div className="userbody">User not found or profile is incomplete</div>;
  }

  return (
    <div className='userbody'>
      <div className="user-container">
        <div className="user-column1">
          <div className="user-grid-item user-grid-1">
            <div className='user-bar'>
              <div className='userdetails userdetails1'>
                {user.profile_pic ? (
                  <img src={user.profile_pic} alt={`${user.first_name}'s profile`} className='userImg' />
                ) : (
                  <img src={defaultimg} alt="Default profile" className='userImg' />
                )}
              </div>
              <div className='userdetails userdetails2'>
                <h2>{user.first_name} {user.last_name || ''}&nbsp;
                  {user.user_type && (
                    <span style={{ fontSize: 'small', fontWeight: '300' }}>({user.user_type})</span>
                  )}
                </h2>
                {(user.course || user.mat_year) && (
                  <p className='userdesc'>{user.course} {user.mat_year ? `Year ${user.mat_year}` : ''}</p>
                )}
                {user.tagline && <p className='userdesc'>{user.tagline}</p>}
                {user.cv_link && (
                  <p className='userdesc' style={{ marginBottom: '0.5rem' }}>
                    CV: &nbsp;<a style={{ color: 'black' }} href={user.cv_link} target='_blank' rel="noopener noreferrer">
                      <i className="fa-regular fa-file" />
                    </a>
                  </p>
                )}
                {user.email && (
                  <a href={`mailto:${user.email}`}>
                    <button className='usercontBtn'>Contact</button>
                  </a>
                )}
                <button 
                  className='usercontBtn' 
                  onClick={() => handleChatClick(user.user_id)}
                >
                  Chat
                </button>
                <button 
                  className='usercontBtn' 
                  style={{ backgroundColor: '#DC2626' }}
                  onClick={() => setShowReportDialog(true)}
                >
                  Report Profile
                </button>
              </div>
            </div>
            <hr />
            <p className='userdesc' style={{ marginTop: '1.5rem' }}>
              {user.description || 'No description available'}
            </p>
          </div>

          <div className="user-grid-item user-grid-2">
            <h3>Portfolio</h3><hr /><br />
            <div className='portfolioBars'>
              {portfolioTitlesArray.length > 0 ? (
                portfolioTitlesArray.map((title, index) => (
                  <div key={index} className='myportBar'>
                    <a 
                      href={portfoliohyperlinksArray[index] ? 
                        (portfoliohyperlinksArray[index].startsWith('http') ? 
                          portfoliohyperlinksArray[index] : 
                          `https://${portfoliohyperlinksArray[index]}`) : 
                        '#'} 
                      target="_blank" 
                      rel="noopener noreferrer"
                    >
                      <img src={android} alt="" className='myportImg' />
                    </a>
                    <div className='portfolioText'>
                      <span style={{ marginLeft: '0.6rem' }}>{title}</span>
                    </div>
                  </div>
                ))
              ) : (
                <div className="no-portfolio">
                  No Portfolio Added
                </div>
              )}
            </div>
          </div>

          <div className="user-grid-item user-grid-3">
            <h3 style={{ marginBottom: '1.5rem' }}>Experience<hr /></h3>
            {expdetails.length > 0 ? (
              <div>
                {expdetails.map((experience, index) => (
                  <div key={experience.experience_id || index}>
                    <div style={{ marginBottom: '1rem' }}>
                      <div>
                        <span>{experience.title}</span>
                        <span style={{ float: 'right', fontWeight: '300' }}>
                          {formatDate(experience.start_date)} - {formatDate(experience.end_date)}
                        </span>
                      </div>
                      <p style={{ fontWeight: '300', margin: '0.5rem 0' }}>{experience.company}</p>
                      <p style={{ fontSize: '0.9rem', fontWeight: '300' }}>
                        {experience.tasks}
                      </p>
                    </div>
                    {index !== expdetails.length - 1 && (
                      <hr style={{ marginBottom: '1rem', color: 'grey', height: '0.5px', backgroundColor: 'lightgrey', border: 'none' }} />
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div>
                <span>No Experience Added</span>
              </div>
            )}
          </div>

          <div className="user-grid-item user-grid-4">
            <h3 style={{ marginBottom: '1.5rem' }}>Education<hr /></h3>
            {edudetails.length > 0 ? (
              <div>
                {edudetails.map((education, index) => (
                  <div key={education.education_id || index} style={{ marginBottom: '1rem' }}>
                    <div style={{ marginBottom: '1rem' }}>
                      <p>
                        {education.title}
                        <span style={{ float: 'right', fontWeight: '300' }}>
                          {formatDate(education.start_date)} - {formatDate(education.end_date)}
                        </span>
                      </p>
                      <p style={{ fontWeight: '300', margin: '0.5rem 0' }}>{education.school}</p>
                      <p style={{ fontSize: '0.9rem', fontWeight: '300' }}>
                        {education.tasks}
                      </p>
                    </div>
                    {index !== edudetails.length - 1 && (
                      <hr style={{ marginBottom: '1rem', color: 'black', height: '1px', backgroundColor: 'black' }} />
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div>
                <span>No Education Added</span>
              </div>
            )}
          </div>

          <div className="user-grid-item user-grid-5">
            <h3 style={{ marginBottom: '1.5rem' }}>Certification<hr /></h3>
            {certdetails.length > 0 ? (
              <div>
                {certdetails.map((certification, index) => (
                  <div key={certification.certification_id || index} style={{ marginBottom: '1rem' }}>
                    <div>
                      <span>{certification.title}</span>
                      <span style={{ float: 'right', fontWeight: '300' }}>
                        {formatDate(certification.start_date)} - {formatDate(certification.end_date)}
                      </span>
                    </div>
                    <div style={{ marginBottom: '3rem' }}>
                      <p style={{ fontWeight: '300', margin: '0.5rem 0' }}>{certification.company}</p>
                      {certification.cert_link && (
                        <p>
                          <a 
                            href={certification.cert_link.startsWith('http') ? certification.cert_link : `https://${certification.cert_link}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ fontSize: 'small', color: '#0066CC', float: 'left', fontWeight: '300' }}
                          >
                            View Credentials &gt;
                          </a>
                        </p>
                      )}
                    </div>
                    {index !== certdetails.length - 1 && (
                      <hr style={{ marginBottom: '1rem', color: 'black', height: '1px', backgroundColor: 'black' }} />
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div>
                <span>No Certification Added</span>
              </div>
            )}
          </div>
        </div>
        <div className="user-column2">
          <div className="user-grid-item user-grid-6">
            <h3 style={{ fontWeight: '400', marginBottom: '-0.4rem' }}>Projects</h3><br />
            {projectTitlesArray.length > 0 ? (
              projectTitlesArray.map((title, index) => (
                <div key={index} className='userproj'>
                  <p className='userprojtitle'>{title}</p>
                  {projectIDsArray && projectIDsArray[index] && (
                    <a href={`/project/${projectIDsArray[index]}`} className='userprojbtn'>View &gt;</a>
                  )}
                </div>
              ))
            ) : (
              <div>
                <p>No Projects Added</p>
              </div>
            )}
          </div>
          <div className="user-grid-item user-grid-7">
            <h3>Top Skills</h3><br />
            {topSkillsArray.length > 0 ? (
              <div>
                <div className='userskillstags'>
                  {topSkillsArray.map((skill, index) => (
                    <a key={index} href="#" className='userskillstag' onClick={(e) => e.preventDefault()}>
                      {skill}
                    </a>
                  ))}
                </div>
              </div>
            ) : (
              <div>
                <p>No Skills Added</p>
              </div>
            )}
          </div>
        </div>
      </div>
      {showReportDialog && (
        <div className="modal-overlay" onClick={() => setShowReportDialog(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h2 style={{ marginBottom: '1.5rem' }}>Report Profile</h2>
            <div className="form-group">
              <label>Reason for Report</label>
              <select 
                className="form-select"
                value={reportReason}
                onChange={(e) => setReportReason(e.target.value)}
              >
                <option value="">Select a reason</option>
                <option value="inappropriate_content">Inappropriate Content</option>
                <option value="fake_information">Fake Information</option>
                <option value="impersonation">Impersonation</option>
                <option value="offensive_material">Offensive Material</option>
                <option value="spam">Spam</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div className="form-group">
              <label>Description</label>
              <textarea
                className="form-textarea"
                value={reportDescription}
                onChange={(e) => setReportDescription(e.target.value)}
                placeholder="Please provide more details about the issue..."
                rows={4}
              />
            </div>
            <div className="modal-buttons">
              <button 
                className="modal-button cancel"
                onClick={() => setShowReportDialog(false)}
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button 
                className="modal-button submit"
                onClick={handleReport}
                disabled={isSubmitting || !reportReason || !reportDescription}
              >
                {isSubmitting ? 'Submitting...' : 'Submit Report'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserContent;